import { motion } from "framer-motion";
import { MessageSquare, Phone, Mail, Plus } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { CONTACT } from "@/components/Footer";
import { BookingRequestForm } from "@/components/BookingRequestForm";
import { TID } from "@/lib/testIds";

const SMS_TEMPLATE =
  "Hi Owen, I'm interested in booking for my player at Coast 2 Coast. Their age is ___ and they'd like to do ___. When are you free?";
const SMS_HREF = `sms:${CONTACT.phoneRaw}?body=${encodeURIComponent(SMS_TEMPLATE)}`;

const EMAIL_SUBJECT = "Coast 2 Coast — new inquiry";
const EMAIL_BODY =
  "Hi Owen,\n\nI'd love to book for my player at Coast 2 Coast.\n\n" +
  "Child's name / age:\nProgram of interest:\nAvailability:\n\nThanks!";
const EMAIL_HREF = `mailto:${CONTACT.email}?subject=${encodeURIComponent(
  EMAIL_SUBJECT,
)}&body=${encodeURIComponent(EMAIL_BODY)}`;

const FAQS = [
  {
    q: "What ages do you coach?",
    a: "I coach players of all ages up to 18. All levels — from first time picking up a ball to rep-team players sharpening their edge.",
  },
  {
    q: "Where do sessions actually happen?",
    a: "Around Sydney's Eastern Suburbs and inner-south — Randwick, Bondi, Maroubra and Alexandria. I lock in the court and share the exact location once we're booked.",
  },
  {
    q: "How do I actually book a session?",
    a: "Fill out the booking request form above with your player's details and I'll text you back to lock in session times. Prefer to chat first? Text me on 0458 792 779.",
  },
  {
    q: "What does it cost?",
    a: "Individual sessions are $100, and the 1-on-1 10-session package is $900 (that's a $100 saving). Group training runs as a 10-session deal: $500 per player for groups of 2–4, or $400 per player for groups of 5–8. Holiday camp pricing lands in 2026.",
  },
  {
    q: "My player has never played basketball. Is that okay?",
    a: "Absolutely. Beginners are welcome. The first session I take time to assess where they're at, then we build a plan around confidence first, skill second — no one gets thrown in the deep end.",
  },
  {
    q: "What should we bring to a session?",
    a: "Basketball shoes (or clean runners), a water bottle, and your own basketball. Bringing your own ball is a must — every session focuses on ball-in-hand reps.",
  },
  {
    q: "What's included in the 10-session package?",
    a: "Ten locked-in 60-minute sessions, priority slot selection, and a proper development plan tailored to your player's goals. All 10 sessions must be used within a 12-week period. Best value for parents who are serious about consistent progress.",
  },
  {
    q: "How does the group session work?",
    a: "Group training runs as a 10-session deal for groups of 2–8 — bring your mates or teammates. Sessions run 60 minutes and focus on live decision-making and competitive reps under pressure. You book the first session online and we lock in the rest together.",
  },
  {
    q: "Do you offer holiday camps?",
    a: "Dedicated Coast 2 Coast holiday camps are launching in 2027 — multi-day skill blocks during school breaks. Text me to jump on the waitlist and I'll flag your player as soon as spots open.",
  },
];

const rise = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-15%" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export const InquiryForm = () => {
  return (
    <section
      id="contact"
      data-testid={TID.contact.root}
      className="relative py-32 md:py-48 px-6 md:px-12 bg-[#060b14]"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Section header */}
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-4">
            <div className="chapter-num mb-6">Book a session</div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <motion.h2
              {...rise}
              className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight"
            >
              Let&apos;s get
              <br />
              them <span className="text-[#2a5fdb]">on court.</span>
            </motion.h2>
            <motion.p
              {...rise}
              transition={{ ...rise.transition, delay: 0.1 }}
              className="mt-8 text-white/70 max-w-xl text-base md:text-lg leading-relaxed"
            >
              Fill in the quick form below and Owen will text you back to lock
              in session times. All sessions run 60 minutes.
            </motion.p>
          </div>
        </div>

        {/* Rep programs shortcut */}
        <motion.a
          {...rise}
          href="#whats-new"
          data-testid="rep-shortcut-box"
          className="group mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-[#2a5fdb]/50 bg-[#2a5fdb]/10 hover:bg-[#2a5fdb]/20 transition-colors px-6 py-5"
        >
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#2a5fdb]">
              New · 6-week block · U12–U18
            </div>
            <div className="font-display uppercase text-xl md:text-2xl tracking-tight mt-1">
              Next Step Rep Program
            </div>
            <p className="text-white/60 text-sm mt-1">
              Wed 6:30–7:30am · starts 30 Sep · $150 upfront for all
              6 weeks (works out to $25 a session)
            </p>
          </div>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#2a5fdb] group-hover:text-white transition-colors shrink-0">
            Claim a spot ↑
          </span>
        </motion.a>

        {/* Booking request form */}
        <motion.div
          {...rise}
          transition={{ ...rise.transition, delay: 0.15 }}
          data-testid="home-booking-widget"
        >
          <BookingRequestForm />
        </motion.div>

        {/* Slim direct-contact row */}
        <motion.div
          {...rise}
          transition={{ ...rise.transition, delay: 0.1 }}
          className="mt-10 pt-8 border-t border-white/10 flex flex-wrap items-center gap-x-8 gap-y-4"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
            Prefer to chat first?
          </span>
          <a
            href={SMS_HREF}
            data-testid="contact-sms"
            className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/80 hover:text-[#2a5fdb] transition-colors"
          >
            <MessageSquare size={14} strokeWidth={1.5} /> Text {CONTACT.phone}
          </a>
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            data-testid="contact-call"
            className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/80 hover:text-[#2a5fdb] transition-colors"
          >
            <Phone size={14} strokeWidth={1.5} /> Call
          </a>
          <a
            href={EMAIL_HREF}
            data-testid="contact-email-cta"
            className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/80 hover:text-[#2a5fdb] transition-colors"
          >
            <Mail size={14} strokeWidth={1.5} /> {CONTACT.email}
          </a>
        </motion.div>

        {/* FAQ */}
        <div
          id="faq"
          data-testid="faq-section"
          className="mt-24 md:mt-32 grid grid-cols-12 gap-6 md:gap-12 items-start"
        >
          <div className="col-span-12 md:col-span-4">
            <div className="chapter-num mb-4">FAQ</div>
            <motion.h3
              {...rise}
              className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight"
            >
              Common
              <br />
              <span className="text-[#2a5fdb]">questions.</span>
            </motion.h3>
            <p className="mt-6 text-white/60 text-sm leading-relaxed max-w-sm">
              Anything not covered? Text me — I&apos;ll answer directly and
              usually within a few hours.
            </p>
          </div>

          <div className="col-span-12 md:col-span-8">
            <Accordion
              type="single"
              collapsible
              className="border-t border-white/10"
              data-testid="faq-accordion"
            >
              {FAQS.map((item, i) => (
                <AccordionItem
                  key={item.q}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-b border-white/10 group"
                >
                  <AccordionTrigger className="py-6 md:py-7 text-left hover:no-underline group [&>svg:last-child]:hidden">
                    <span className="flex items-start gap-4 md:gap-6 flex-1 pr-4">
                      <span className="font-mono text-[11px] text-[#2a5fdb] tracking-[0.15em] pt-1 shrink-0 w-6">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-display uppercase text-lg md:text-2xl tracking-tight text-white leading-[1.15] group-hover:text-[#2a5fdb] transition-colors duration-300">
                        {item.q}
                      </span>
                    </span>
                    <Plus
                      size={20}
                      strokeWidth={1.5}
                      className="text-white/60 shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45 group-hover:text-white"
                    />
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 md:pb-8 pl-10 md:pl-12 pr-4">
                    <p className="text-white/70 text-base leading-relaxed max-w-2xl">
                      {item.a}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};
